import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowDownRight, ArrowUpRight, Eye, Loader2, Pencil, Plus, RotateCcw,
  SlidersHorizontal, Sparkles, Trash2,
} from "lucide-react";
import { api, errMsg } from "../lib/api";
import { formatDateID, formatRupiah } from "../lib/format";
import {
  ConfirmDialog, EmptyState, PaginationBar, TypeBadge, tdClass, thClass,
} from "../components/Common";
import TransactionModal from "../components/TransactionModal";
import { useAuth } from "../context/AuthContext";
import AdjustExpenseModal from "../components/AdjustExpenseModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const PAGE_META = {
  income: {
    title: "Pemasukan", addLabel: "Tambah Pemasukan", addTestId: "btn-tambah-pemasukan",
    tableTestId: "table-pemasukan", partyLabel: "Sumber Dana",
    emptyText: "Belum ada pemasukan tercatat. Tambahkan pemasukan pertama lewat Quick Input.",
  },
  expense: {
    title: "Pengeluaran", addLabel: "Tambah Pengeluaran", addTestId: "btn-tambah-pengeluaran",
    tableTestId: "table-pengeluaran", partyLabel: "Penerima / Tujuan",
    emptyText: "Belum ada pengeluaran tercatat. Tambahkan pengeluaran pertama lewat Quick Input.",
  },
  all: {
    title: "Semua Transaksi", addLabel: null, addTestId: null,
    tableTestId: "table-semua-transaksi", partyLabel: "Sumber/Penerima",
    emptyText: "Belum ada transaksi. Tambahkan transaksi pertama lewat Quick Input.",
  },
};

export default function Transactions({ type = "all" }) {
  const meta = PAGE_META[type];
  const { hasPerm } = useAuth();
  const canCreate =
    type === "income" ? hasPerm("create_income")
      : type === "expense" ? hasPerm("create_expense")
        : hasPerm("create_income", "create_expense");
  const canEditRow = (t) =>
    t.hidden ? hasPerm("edit_special_expense") : hasPerm(`edit_${t.type}`, "edit_transactions");
  const canDeleteRow = (t) =>
    t.hidden ? hasPerm("delete_special_expense") : hasPerm(`delete_${t.type}`, "delete_transactions");
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [book, setBook] = useState(hasPerm("view_saldo_resmi") ? "resmi" : "imf");

  useEffect(() => {
    api.get("/stats/books").then((r) => {
      if ((r.data.resmi_count ?? 0) === 0 && (r.data.imf_count ?? 0) > 0) setBook("imf");
    }).catch(() => {});
  }, []);
  const [category, setCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("date-desc");
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState({ open: false, tx: null });
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    setQ(searchParams.get("q") || "");
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    setQ(""); setCategory(""); setDateFrom(""); setDateTo(""); setSort("date-desc"); setPage(1);
  }, [type]);

  useEffect(() => {
    api.get(type === "all" ? "/categories" : `/categories?type=${type}`)
      .then((r) => setCategories(r.data))
      .catch(() => {});
  }, [type]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sortField, sortOrder] = sort.split("-");
      const params = new URLSearchParams({
        type: type === "all" ? "" : type,
        q, category, date_from: dateFrom, date_to: dateTo, book,
        sort: sortField, order: sortOrder, page: String(page), limit: String(limit),
      });
      const { data } = await api.get(`/transactions?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [type, q, category, dateFrom, dateTo, sort, page, limit, book]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const resetFilters = () => {
    setQ(""); setCategory(""); setDateFrom(""); setDateTo(""); setSort("date-desc"); setPage(1);
  };

  const doDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/transactions/${deleting.id}`);
      toast.success("Transaksi berhasil dihapus. Saldo diperbarui otomatis.");
      setDeleting(null);
      load();
      window.dispatchEvent(new Event("saldo:refresh"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleteLoading(false);
    }
  };

  const seedSample = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/seed/sample");
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSeeding(false);
    }
  };

  const hasFilter = useMemo(() => q || category || dateFrom || dateTo, [q, category, dateFrom, dateTo]);
  const showParty = type !== "all";

  return (
    <div className="space-y-4" data-testid={`page-${type}`}>
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-2">
          <input
            data-testid="input-filter-search"
            className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-600"
            placeholder="Cari uraian / no bukti…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
          />
          <select
            data-testid="select-filter-category"
            className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          >
            <option value="">Semua Kategori</option>
            {categories.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
          {hasPerm("view_saldo_resmi") && hasPerm("view_saldo_imf") && (
            <select
              data-testid="select-filter-book"
              className="rounded-lg border border-blue-500/30 bg-blue-500/15 px-3 py-2 text-sm font-semibold text-blue-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
              value={book}
              onChange={(e) => { setBook(e.target.value); setPage(1); }}
            >
              <option value="resmi">Kas Resmi</option>
              <option value="imf">Kas IMF</option>
            </select>
          )}
          <input data-testid="input-filter-date-from" type="date" className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
          <span className="text-xs text-slate-400">s/d</span>
          <input data-testid="input-filter-date-to" type="date" className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
          <select
            data-testid="select-sort"
            className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="date-desc">Tanggal Terbaru</option>
            <option value="date-asc">Tanggal Terlama</option>
            <option value="amount-desc">Nominal Tertinggi</option>
            <option value="amount-asc">Nominal Terendah</option>
          </select>
          {hasFilter && (
            <button data-testid="btn-reset-filter" onClick={resetFilters} className="text-xs font-semibold text-slate-400 hover:text-slate-100 flex items-center gap-1 px-2 py-2">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
          <div className="flex-1" />
          {type === "expense" && hasPerm("edit_expense") && (
            <button
              data-testid="btn-adjust-expense"
              onClick={() => setAdjustOpen(true)}
              className="border border-slate-700 bg-[#1E293B] text-slate-200 hover:bg-slate-800/40 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm shadow-sm"
            >
              <SlidersHorizontal className="w-4 h-4" /> Penyesuaian Nominal
            </button>
          )}
          {meta.addLabel && canCreate && (
            <button
              data-testid={meta.addTestId}
              onClick={() => setModal({ open: true, tx: null })}
              className={`${type === "income" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"} text-white font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm shadow-sm`}
            >
              <Plus className="w-4 h-4" /> {meta.addLabel}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>
      ) : total === 0 ? (
        <EmptyState
          title={hasFilter ? "Tidak ada hasil" : `Belum ada data ${meta.title.toLowerCase()}`}
          description={hasFilter ? "Coba ubah kata kunci atau filter yang digunakan." : meta.emptyText}
          action={null}
        />
      ) : (
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid={meta.tableTestId} className="w-full border-collapse text-sm text-left">
              <thead>
                <tr>
                  <th className={thClass}>No</th>
                  <th className={thClass}>Tanggal</th>
                  {type === "all" && <th className={thClass}>Jenis</th>}
                  <th className={thClass}>No Bukti</th>
                  <th className={thClass}>Uraian</th>
                  <th className={thClass}>Kategori</th>
                  {showParty && <th className={thClass}>{meta.partyLabel}</th>}
                  <th className={`${thClass} text-right`}>Nominal</th>
                  {type === "all" && <th className={`${thClass} text-right`}>Saldo Berjalan</th>}
                  <th className={thClass}>Keterangan</th>
                  <th className={`${thClass} text-center`}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t, i) => (
                  <tr key={t.id} className="hover:bg-slate-800/40" data-testid={`row-transaction-${t.id}`}>
                    <td className={tdClass}>{(page - 1) * limit + i + 1}</td>
                    <td className={`${tdClass} whitespace-nowrap`}>{formatDateID(t.date)}</td>
                    {type === "all" && <td className={tdClass}><TypeBadge type={t.type} /></td>}
                    <td className={`${tdClass} font-num text-xs`}>{t.transaction_number}</td>
                    <td className={`${tdClass} font-medium text-slate-100 max-w-[240px]`}>
                      <span className="line-clamp-1">{t.description}</span>
                    </td>
                    <td className={tdClass}>
                      <span className="bg-slate-800 text-slate-200 rounded-full px-2.5 py-0.5 text-xs font-medium">{t.category}</span>
                    </td>
                    {showParty && <td className={tdClass}>{t.party || "-"}</td>}
                    <td className={`${tdClass} text-right font-num font-semibold whitespace-nowrap ${t.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                      {type === "all" && (t.type === "income" ? "+" : "−")}{formatRupiah(t.amount)}
                    </td>
                    {type === "all" && (
                      <td className={`${tdClass} text-right font-num font-semibold text-blue-300 whitespace-nowrap`}>
                        {formatRupiah(t.running_balance)}
                      </td>
                    )}
                    <td className={`${tdClass} max-w-[160px]`}><span className="line-clamp-1 text-slate-400">{t.notes || "-"}</span></td>
                    <td className={`${tdClass} text-center`}>
                      <div className="flex items-center justify-center gap-1">
                        <button data-testid={`btn-detail-${t.id}`} title="Detail" onClick={() => setDetail(t)} className="p-1.5 rounded-md hover:bg-slate-800/40 text-slate-400 hover:text-slate-100"><Eye className="w-4 h-4" /></button>
                        {canEditRow(t) && (
                          <button data-testid={`btn-edit-${t.id}`} title="Edit" onClick={() => setModal({ open: true, tx: t })} className="p-1.5 rounded-md hover:bg-slate-800/40 text-slate-400 hover:text-blue-300"><Pencil className="w-4 h-4" /></button>
                        )}
                        {canDeleteRow(t) && (
                          <button data-testid={`btn-delete-${t.id}`} title="Hapus" onClick={() => setDeleting(t)} className="p-1.5 rounded-md hover:bg-rose-500/15 text-slate-400 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBar page={page} pages={pages} total={total} onPage={setPage} limit={limit} onLimit={(n) => { setLimit(n); setPage(1); }} />
        </div>
      )}

      <TransactionModal
        open={modal.open}
        onOpenChange={(o) => setModal((m) => ({ ...m, open: o }))}
        type={type === "all" ? modal.tx?.type || "income" : type}
        transaction={modal.tx}
        onSaved={() => { load(); window.dispatchEvent(new Event("saldo:refresh")); }}
      />
      <AdjustExpenseModal open={adjustOpen} onOpenChange={setAdjustOpen} onApplied={() => { load(); window.dispatchEvent(new Event("saldo:refresh")); }} />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Hapus Transaksi?"
        description={deleting ? `Transaksi "${deleting.description}" (${formatRupiah(deleting.amount)}) akan dihapus permanen. Saldo akan dihitung ulang otomatis.` : ""}
        onConfirm={doDelete}
        loading={deleteLoading}
      />

      <Dialog open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="modal-transaction-detail" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Detail Transaksi</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <TypeBadge type={detail.type} />
                <span className={`font-num text-xl font-bold ${detail.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                  {detail.type === "income" ? "+" : "−"}{formatRupiah(detail.amount)}
                </span>
              </div>
              <div className="rounded-lg border border-slate-800 divide-y divide-slate-800">
                {[
                  ["Tanggal", formatDateID(detail.date)],
                  ["No Bukti", detail.transaction_number],
                  ["Uraian", detail.description],
                  ["Kategori", detail.category],
                  [detail.type === "income" ? "Sumber Dana" : "Penerima/Tujuan", detail.party || "-"],
                  ["Keterangan", detail.notes || "-"],
                  ["Saldo Setelah Transaksi", formatRupiah(detail.running_balance)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 px-3 py-2">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-medium text-slate-100 text-right">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
